import React from 'react'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'
import styles from '../../../styles.module.scss'
import { prescriptionTemplateSchema } from '../../../../../validationSchema'
import { Form, Formik } from 'formik'
import CustomInput from '../../../../../components/CustomInputs/CustomInput'
import { useEffect, useState } from 'react'
import CustomPlusMinusInput from '../../../../../components/CustomInputs/CustomPlusMinusInput'
import CustomTextArea from '../../../../../components/CustomInputs/CustomTextArea'
import { database, databaseRef } from 'utils/firebase'
import { onValue, push, set } from 'firebase/database'
import { useAlert } from 'react-alert'
import { useSelector } from 'react-redux'
import { addDoctorLog } from 'utils/functions'

const AddModal = ({ isOpen, closeModal, edit, editKey }) => {
  const [initialValues, setInitialValues] = useState({
    name: '',
    medicine: {
      name: '',
      composition: '',
      route: '',
      dosage: '',
      frequency: '',
      duration: '',
      quantity: '',
      refills: '',
      instructions: '',
      warning: '',
    },
  })
  const { me } = useSelector((state) => state.app)

  const alert = useAlert()

  useEffect(() => {
    if (edit) {
      const templateRef = databaseRef(database, `templates/medications/${editKey}`)
      const unsubscribe = onValue(templateRef, (snapshot) => {
        setInitialValues(snapshot.val())
      }, { onlyOnce: true })
      
      return () => unsubscribe()
    }
  }, [edit, editKey])

  const handleEditSubmit = async (values, action) => {
    const templateRef = databaseRef(database, `templates/medications/${editKey}`)
    try {
      await set(templateRef, values)
      addDoctorLog({
        action: 'UPDATE_MEDICATION_TEMPLATE',
        category: 'template',
        description: `Updated medication template: ${values.name}`,
        doctorID: me.id,
        details: {
          templateId: editKey,
          templateName: values.name,
          timestamp: Date.now()
        }
      })
      closeModal()
      alert.success('Updated')
    } catch (error) {
      // Error handling
    }
  }
  
  const handleSubmit = async (values, action) => {
    const medicationsRef = databaseRef(database, 'templates/medications')
    const newRef = push(medicationsRef)
    try {
      await set(newRef, values)
      addDoctorLog({
        action: 'CREATE_MEDICATION_TEMPLATE',
        category: 'template',
        description: `Created new medication template: ${values.name}`,
        doctorID: me.id,
        details: {
          templateId: newRef.key,
          templateName: values.name,
          timestamp: Date.now()
        }
      })
      closeModal()
      alert.success('Added')
    } catch (error) {
      // Error handling
    }
  }

  return (
    <Modal isOpen={isOpen} centered size="lg">
      <ModalHeader toggle={closeModal}>
        <span className={styles.title}>
          {edit ? 'Edit' : 'New'} Medication Template
        </span>
      </ModalHeader>
      <ModalBody>
        <div>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={edit ? handleEditSubmit : handleSubmit}
            validationSchema={prescriptionTemplateSchema}
          >
            {(FormikProps) => (
              <Form>
                <div className="mb-4">
                  <CustomInput
                    label="Template Name"
                    name="name"
                    placeholder="Amoxicillin 50 mg "
                  />
                </div>
                <div className="mb-4">
                  <div className={styles.medFormRoot}>
                    <div className={styles.medicationInputParent}>
                      <span className={styles.medLabel}>Name:</span>
                      {/* <Field name={`medications[${index}].name`} type="text" className={styles.MedicationInput} placeholder="Amoxicillin 30 mg" /> */}
                      <CustomInput
                        name="medicine.name"
                        type="text"
                        className={styles.MedicationInput}
                        placeholder="Amoxxillin 30 mg"
                      />
                    </div>
                    <div className={styles.medicationInputParent}>
                      <span className={styles.medLabel}>Composition:</span>
                      {/* <Field name={`medications[${index}].composition`} type="text" className={styles.MedicationInput} placeholder="Tablet" /> */}
                      <CustomInput
                        name="medicine.composition"
                        type="text"
                        className={styles.MedicationInput}
                        placeholder="Tablet"
                      />
                    </div>
                    <div className={styles.medicationInputParent}>
                      <span className={styles.medLabel}>Route:</span>
                      <CustomInput
                        name="medicine.route"
                        type="text"
                        className={styles.MedicationInput}
                        placeholder="Oral"
                      />
                    </div>

                    <div className={styles.medicationInputParent}>
                      <span className={styles.medLabel}>Dosage:</span>
                      <CustomInput
                        name="medicine.dosage"
                        type="text"
                        className={styles.MedicationInput}
                        placeholder="500 mg"
                      />
                    </div>
                    <div className={styles.medicationInputParent}>
                      <span className={styles.medLabel}>Frequency:</span>
                      <CustomInput
                        name="medicine.frequency"
                        type="text"
                        className={styles.MedicationInput}
                        placeholder="Twice a day"
                      />
                    </div>
                    <div className={styles.medicationInputParent}>
                      <span className={styles.medLabel}>Duration:</span>
                      <CustomInput
                        name="medicine.duration"
                        type="text"
                        className={styles.MedicationInput}
                        placeholder="3 days"
                      />
                    </div>
                    <div className={styles.medicationInputParent}>
                      <span className={styles.medLabel}>Total Quantity:</span>
                      <CustomInput
                        name="medicine.quantity"
                        type="text"
                        className={styles.MedicationInput}
                        placeholder="30 tablets"
                      />
                    </div>
                    <div className={styles.medicationInputParent}>
                      <span className={styles.medLabel}>Refills:</span>
                      <CustomPlusMinusInput
                        name="medicine.refills"
                        type="text"
                        className={styles.MedicationInput}
                        placeholder="0"
                      />
                    </div>

                    <div className={styles.medicationInputParent}>
                      <span className={styles.medLabel}>Instructions:</span>
                      <CustomTextArea
                        name="medicine.instructions"
                        className={styles.MedicationTextArea}
                        placeholder="Take with food"
                        minHeight="30px"
                      />
                    </div>

                    <div className={styles.medicationInputParent}>
                      <span className={styles.medLabel}>Warning:</span>
                      <CustomTextArea
                        name="medicine.warning"
                        className={styles.MedicationTextArea}
                        placeholder="Do not recommend if patient has kidney issues..."
                        minHeight="30px"
                      />
                    </div>
                  </div>
                </div>
                <hr />
                <div className="d-flex justify-content-end align-items-center">
                  <button
                    type="submit"
                    className={styles.SaveBtn}
                    disabled={FormikProps.isSubmitting}
                  >
                    {edit ? 'Update' : 'Submit'} template
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default AddModal
